import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAppContext } from '../contexts/AppContext';
import { useTour } from '../contexts/TourContext';
import { useLearn } from '../contexts/LearnContext';
import { getDfmeaAppUrl, getResolveAppUrl } from '../config/appUrls';
import MagnusAssistant from './MagnusAssistant';
import PatentPendingIcon from './PatentPendingIcon';
import Footer from './Footer';
import { LearnSidebar } from './learn/LearnSidebar';
import {
    LayoutDashboard,
    GitBranch,
    FileSpreadsheet,
    Sparkles,
    ClipboardCheck,
    FileText,
    Settings,
    Moon,
    Sun,
    LogOut,
    Menu,
    X,
    ChevronDown,
    Shield,
    FolderOpen,
    Link2,
    CheckSquare,
    Bot,
    BarChart2,
    Target,
    HelpCircle,
    Folder,
    Check,
    Cloud,
    Map,
    ExternalLink,
    ShieldAlert,
    Maximize,
    Minimize,
    GraduationCap,
    type LucideIcon
} from 'lucide-react';

// Type for navigation items
type NavLinkItem = { path: string; icon: LucideIcon; label: string; permission: string; tourId?: string; indented?: boolean; external?: boolean };

const Layout: React.FC = () => {
    const { user, logout, hasPermission } = useAuth();
    const { isDarkMode, toggleTheme } = useTheme();
    const { activeProject, activeStandard, isFullScreen, setIsFullScreen } = useAppContext();
    const { startTour } = useTour();
    const { setIsLearnOpen } = useLearn();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
    // State for collapsible nav sections - expand first few by default
    const [expandedNavSections, setExpandedNavSections] = useState<Set<string>>(
        new Set(['🤖 AI Assistants', 'Overview', 'Step 1: Planning'])
    );

    // Toggle section expansion
    const toggleNavSection = (section: string) => {
        setExpandedNavSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    };

    // Listen for Escape key to exit full screen
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFullScreen) {
                setIsFullScreen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullScreen, setIsFullScreen]);

    // Get DFMEA & Resolve app URLs (environment-aware)
    const DFMEA_URL = getDfmeaAppUrl();
    const RESOLVE_URL = getResolveAppUrl();

    // AIAG-VDA 7-Step PFMEA Workflow Navigation
    // AI TOOLS FIRST - This is an AI-Assisted PFMEA Workflow Tool
    const navItems: NavLinkItem[] = [
        // AI TOOLS
        { path: '/ai-assistant', icon: Bot, label: 'Magnus AI Chat', permission: 'ai.generate_pfmea', tourId: 'ai-assistant' },
        // Specialist Agents
        { path: '/ai-agents?agent=process-flow-analysis', icon: Bot, label: 'AI Process Coach', permission: 'ai.generate_pfmea', indented: true },
        { path: '/ai-agents?agent=process-failure-analysis', icon: Bot, label: 'AI Failure Coach', permission: 'ai.generate_pfmea', indented: true },
        { path: '/ai-agents?agent=process-risk-assessment', icon: Bot, label: 'AI Risk Coach', permission: 'ai.generate_pfmea', indented: true },
        { path: '/ai-agents?agent=control-plan-generator', icon: Bot, label: 'AI Control Plan Coach', permission: 'ai.generate_pfmea', indented: true },

        // OVERVIEW
        { path: '/', icon: LayoutDashboard, label: 'Dashboard', permission: 'pfmea.view', tourId: 'dashboard' },

        // STEP 1: PLANNING & PREPARATION
        { path: '/projects', icon: FolderOpen, label: 'Project Select', permission: 'pfmea.view', tourId: 'planning' },
        { path: '/project-scoping', icon: Target, label: '5T Scoping', permission: 'pfmea.view' },
        { path: '/linked-data', icon: Link2, label: 'Linked Data', permission: 'pfmea.view' },
        { path: '/ideation', icon: Sparkles, label: 'Ideation Wizard', permission: 'ai.generate_pfmea', tourId: 'ai-ideation', indented: true },

        // STEP 2: STRUCTURE ANALYSIS
        { path: '/process-flow', icon: GitBranch, label: 'Process Flow', permission: 'process_flow.view', tourId: 'structure' },

        // STEP 3: FUNCTION ANALYSIS
        { path: '/function-matrix', icon: FileSpreadsheet, label: 'Function Matrix', permission: 'pfmea.view', tourId: 'functions' },

        // STEP 4: FAILURE ANALYSIS
        { path: '/failure-chains', icon: Link2, label: 'Failure Chains', permission: 'pfmea.edit', tourId: 'failure-chains' },
        { path: '/pfmea-editor', icon: FileSpreadsheet, label: 'PFMEA Worksheet', permission: 'pfmea.edit', tourId: 'failure' },
        { path: '/failure-net', icon: GitBranch, label: 'Failure Net', permission: 'pfmea.view', indented: true },

        // STEP 5: RISK ANALYSIS
        { path: '/pfmea', icon: BarChart2, label: 'Risk Matrix', permission: 'pfmea.view', tourId: 'risk' },

        // STEP 6: OPTIMIZATION
        { path: '/actions', icon: CheckSquare, label: 'Action Tracker', permission: 'pfmea.view', tourId: 'optimize' },
        { path: '/workflow', icon: Shield, label: 'Approvals', permission: 'pfmea.view', indented: true },

        // STEP 7: RESULTS DOCUMENTATION
        { path: '/control-plan', icon: ClipboardCheck, label: 'Control Plan', permission: 'control_plan.view', tourId: 'document' },
        { path: '/work-instructions', icon: FileText, label: 'Work Instructions', permission: 'work_instruction.view', indented: true },
        { path: '/reports', icon: BarChart2, label: 'Reports', permission: 'pfmea.view' },
        { path: '/compliance-auditor', icon: Shield, label: 'Compliance Auditor', permission: 'pfmea.view' },

        // DFMEA PORTAL
        { path: `${DFMEA_URL}/fmeas`, icon: ShieldAlert, label: 'DFMEA Table', permission: 'pfmea.view', external: true },
        { path: `${DFMEA_URL}/fmea-editor`, icon: FileSpreadsheet, label: 'DFMEA Editor', permission: 'pfmea.view', external: true },
        { path: `${DFMEA_URL}/p-diagram`, icon: GitBranch, label: 'P-Diagrams', permission: 'pfmea.view', external: true },
        { path: `${DFMEA_URL}/risk-analysis`, icon: BarChart2, label: 'Risk Matrix', permission: 'pfmea.view', external: true },
        { path: `${DFMEA_URL}/dashboard`, icon: LayoutDashboard, label: 'Magnus Dashboard', permission: 'pfmea.view', external: true },
        { path: `${DFMEA_URL}/dashboard`, icon: LayoutDashboard, label: 'Magnus Dashboard', permission: 'dfmea.view', external: true },

        // RESOLVE PORTAL
        { path: `${RESOLVE_URL}`, icon: Sparkles, label: 'Magnus Resolve AI', permission: 'resolve.view', external: true },
        { path: `${RESOLVE_URL}/ai-assistant`, icon: Bot, label: 'Intelligence Hub', permission: 'resolve.view', external: true },
    ];

    const navSections = [
        { section: '🤖 AI Assistants', icon: Sparkles, items: navItems.slice(0, 5) },
        { section: 'Overview', icon: LayoutDashboard, items: navItems.slice(5, 6) },
        { section: 'Step 1: Planning', icon: FolderOpen, items: navItems.slice(6, 10) },
        { section: 'Step 2: Structure', icon: GitBranch, items: navItems.slice(10, 11) },
        { section: 'Step 3: Function', icon: FileSpreadsheet, items: navItems.slice(11, 12) },
        { section: 'Step 4: Failure', icon: FileSpreadsheet, items: navItems.slice(12, 15) },
        { section: 'Step 5: Risk', icon: BarChart2, items: navItems.slice(15, 16) },
        { section: 'Step 6: Optimize', icon: CheckSquare, items: navItems.slice(16, 18) },
        { section: 'Step 7: Document', icon: ClipboardCheck, items: navItems.slice(18, 22) },
        { section: '🔗 Connected Apps', icon: ExternalLink, items: navItems.slice(22) },
    ];

    return (
        <div className="min-h-screen bg-[var(--bg-primary)] flex overflow-hidden">
            {/* Sidebar */}
            {!isFullScreen && (
                <aside className={`
          fixed inset-y-0 left-0 z-50 
          ${sidebarOpen ? 'w-64' : 'w-16'} 
          bg-[var(--bg-secondary)] border-r border-[var(--border-color)]
          transition-all duration-300 flex flex-col overflow-y-auto
        `}>
                    {/* Logo - Magnus Core Style */}
                    <div className="h-20 flex items-center justify-between px-4 border-b border-[var(--border-color)]">
                        {sidebarOpen ? (
                            <div className="flex items-center gap-3">
                                {/* Blue rounded square with shield */}
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <Shield className="h-7 w-7 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-bold text-blue-600 leading-tight">
                                        Magnus-PFMEA
                                    </h1>
                                    <p className="text-xs text-gray-500 font-medium">
                                        Process Failure Analysis
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg mx-auto">
                                <Shield className="h-6 w-6 text-white" />
                            </div>
                        )}
                        {sidebarOpen && (
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                            >
                                <X size={20} />
                            </button>
                        )}
                        {!sidebarOpen && (
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="absolute top-24 left-1/2 -translate-x-1/2 p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                            >
                                <Menu size={18} />
                            </button>
                        )}
                    </div>

                    {/* Gradient Accent Bar - Magnus Core Style */}
                    <div className="gradient-header-process"></div>

                    {/* Project Selector */}
                    {sidebarOpen && (
                        <div className="p-4 border-b border-[var(--border-color)]">
                            <button
                                onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                                className="w-full flex items-center justify-between p-3 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-primary)] transition-colors"
                            >
                                <div className="text-left">
                                    <p className="text-xs text-[var(--text-secondary)]">Active Project</p>
                                    <p className="text-sm font-medium text-[var(--text-primary)]">
                                        {activeProject || 'Select Project'}
                                    </p>
                                </div>
                                <ChevronDown size={16} className="text-[var(--text-secondary)]" />
                            </button>

                            {/* Standard Badge */}
                            <div className="mt-2 flex justify-center">
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300">
                                    {activeStandard?.replace('_', ' ') || 'AIAG VDA'}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Navigation */}
                    <nav className="flex-1 p-3">
                        <ul className="space-y-0.5">
                            {navSections.map((sec, secIndex) => {
                                const SectionIcon = sec.icon;
                                const isExpanded = expandedNavSections.has(sec.section);
                                const visibleItems = sec.items.filter(i => hasPermission(i.permission));
                                const hasItems = visibleItems.length > 0;
                                const hasActiveChild = visibleItems.some(item =>
                                    item.external ? false :
                                        item.path.includes('?')
                                            ? location.pathname + location.search === item.path
                                            : location.pathname === item.path
                                );

                                if (!hasItems) return null;

                                return (
                                    <li key={`section-${secIndex}`} className="relative">
                                        {/* Section Header - Parent Menu Item */}
                                        {sidebarOpen ? (
                                            <div
                                                onClick={() => toggleNavSection(sec.section)}
                                                className={`
                                                    group relative flex items-center justify-between gap-3 px-3 py-2.5 my-0.5 rounded-xl
                                                    transition-all duration-200 cursor-pointer
                                                    ${isExpanded ? 'bg-white/50 dark:bg-gray-700/30' : ''}
                                                    hover:bg-white/80 dark:hover:bg-gray-700/50 hover:shadow-md
                                                `}
                                            >
                                                <div className="flex items-center gap-3 flex-1">
                                                    <div className={`
                                                        p-1.5 rounded-lg transition-all duration-200
                                                        ${hasActiveChild
                                                            ? 'bg-blue-100 dark:bg-blue-900/40'
                                                            : 'group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30'
                                                        }
                                                    `}>
                                                        <SectionIcon
                                                            size={18}
                                                            className={`transition-colors duration-200 ${hasActiveChild
                                                                ? 'text-blue-600 dark:text-blue-400'
                                                                : 'text-slate-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                                                                }`}
                                                        />
                                                    </div>
                                                    <span className={`
                                                        font-medium text-sm transition-colors duration-200
                                                        ${hasActiveChild
                                                            ? 'text-blue-700 dark:text-blue-300 font-semibold'
                                                            : 'text-slate-700 dark:text-gray-200 group-hover:text-slate-900 dark:group-hover:text-white'
                                                        }
                                                    `}>
                                                        {sec.section}
                                                    </span>
                                                </div>

                                                {/* Expand/Collapse Icon */}
                                                <div className={`
                                                    transition-all duration-300 p-1 rounded-lg
                                                    ${isExpanded
                                                        ? 'rotate-0 bg-blue-100 dark:bg-blue-900/40'
                                                        : '-rotate-90 group-hover:bg-slate-100 dark:group-hover:bg-gray-600'
                                                    }
                                                `}>
                                                    <ChevronDown size={14} className={`
                                                        ${hasActiveChild
                                                            ? 'text-blue-600 dark:text-blue-400'
                                                            : 'text-slate-500 dark:text-gray-400'
                                                        }
                                                    `} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="pt-3 pb-1">
                                                <div className="w-8 h-px bg-[var(--border-color)] mx-auto" />
                                            </div>
                                        )}

                                        {/* Submenu with Smooth Transition */}
                                        <div className={`
                                            overflow-hidden transition-all duration-300 ease-in-out
                                            ${(isExpanded || !sidebarOpen) ? 'max-h-[32rem] opacity-100 mt-1' : 'max-h-0 opacity-0'}
                                        `}>
                                            <ul className={`${sidebarOpen ? 'ml-4 pl-4 border-l-2 border-blue-200 dark:border-blue-800/50' : ''} space-y-0.5`}>
                                                {visibleItems.map((item) => {
                                                    const isActive = item.external
                                                        ? false
                                                        : item.path.includes('?')
                                                            ? location.pathname + location.search === item.path
                                                            : location.pathname === item.path;
                                                    const Icon = item.icon;

                                                    // External links
                                                    if (item.external) {
                                                        return (
                                                            <li key={item.path}>
                                                                <a
                                                                    href={item.path}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className={`
                                                                        group relative flex items-center gap-2 px-3 py-2 rounded-lg
                                                                        transition-all duration-200 cursor-pointer
                                                                        hover:bg-slate-100 dark:hover:bg-gray-700/40 hover:translate-x-1
                                                                        ${item.indented ? 'text-xs' : 'text-sm'}
                                                                    `}
                                                                >
                                                                    <div className="w-6 h-6 rounded-md flex items-center justify-center bg-slate-200 dark:bg-gray-600 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40">
                                                                        <Icon size={14} className="text-slate-600 dark:text-gray-300" />
                                                                    </div>
                                                                    {sidebarOpen && (
                                                                        <span className="text-slate-600 dark:text-gray-300 group-hover:text-slate-900 dark:group-hover:text-white flex items-center gap-1">
                                                                            {item.label}
                                                                            <ExternalLink size={10} className="opacity-50" />
                                                                        </span>
                                                                    )}
                                                                </a>
                                                            </li>
                                                        );
                                                    }

                                                    // Internal NavLinks
                                                    return (
                                                        <li key={item.path}>
                                                            <NavLink
                                                                to={item.path}
                                                                data-tour-id={item.tourId}
                                                                className={`
                                                                    group relative flex items-center gap-2 px-3 py-2 rounded-lg
                                                                    transition-all duration-200 cursor-pointer
                                                                    ${item.indented ? 'text-xs' : 'text-sm'}
                                                                    ${isActive
                                                                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-700 dark:text-blue-300 shadow-sm font-medium scale-[1.01]'
                                                                        : 'hover:bg-slate-100 dark:hover:bg-gray-700/40 hover:translate-x-1'
                                                                    }
                                                                `}
                                                            >
                                                                {/* Child Icon */}
                                                                <div className={`
                                                                    w-6 h-6 rounded-md flex items-center justify-center transition-all
                                                                    ${isActive
                                                                        ? 'bg-blue-500 shadow-md'
                                                                        : 'bg-slate-200 dark:bg-gray-600 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/40'
                                                                    }
                                                                `}>
                                                                    <Icon size={14} className={isActive ? 'text-white' : 'text-slate-600 dark:text-gray-300'} />
                                                                </div>

                                                                {/* Child Name */}
                                                                {sidebarOpen && (
                                                                    <span className={`
                                                                        transition-colors
                                                                        ${isActive
                                                                            ? 'text-blue-700 dark:text-blue-300 font-medium'
                                                                            : 'text-slate-600 dark:text-gray-300 group-hover:text-slate-900 dark:group-hover:text-white'
                                                                        }
                                                                    `}>
                                                                        {item.label}
                                                                    </span>
                                                                )}

                                                                {/* Active Dot */}
                                                                {isActive && sidebarOpen && (
                                                                    <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                                                )}
                                                            </NavLink>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>

                    {/* Bottom Actions */}
                    <div className="p-4 border-t border-[var(--border-color)] space-y-2">
                        <NavLink
                            to="/settings"
                            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all
              ${location.pathname === '/settings'
                                    ? 'bg-primary-500 text-white'
                                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                                }
            `}
                        >
                            <Settings size={20} />
                            {sidebarOpen && <span className="font-medium">Settings</span>}
                        </NavLink>

                        <button
                            onClick={toggleTheme}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-all"
                        >
                            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                            {sidebarOpen && <span className="font-medium">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>}
                        </button>

                        <button
                            onClick={logout}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                        >
                            <LogOut size={20} />
                            {sidebarOpen && <span className="font-medium">Logout</span>}
                        </button>
                    </div>

                    {/* User Info */}
                    {sidebarOpen && user && (
                        <div className="p-4 border-t border-[var(--border-color)]">
                            <div className="flex items-center gap-3">
                                {user.photoURL ? (
                                    <img src={user.photoURL} alt="" className="w-10 h-10 rounded-full" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-bold">
                                        {user.displayName?.charAt(0) || 'U'}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                                        {user.displayName}
                                    </p>
                                    <p className="text-xs text-[var(--text-secondary)] truncate">
                                        {user.role}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </aside>
            )}

            {/* Main Content Area */}
            <div className={`
                flex-1 transition-all duration-300 flex flex-col h-screen
                ${isFullScreen ? 'ml-0' : sidebarOpen ? 'ml-64' : 'ml-16'}
            `}>
                {/* Top Header Bar */}
                <header className="flex-shrink-0 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-sm">
                    <div className="flex items-center justify-between px-4 py-3">
                        {/* Left side - Page info */}
                        <div className="flex items-center gap-4">
                            {isFullScreen && (
                                <button
                                    onClick={() => setIsFullScreen(false)}
                                    className="p-2 mr-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-all flex items-center gap-2"
                                    title="Exit Full Screen (Esc)"
                                >
                                    <X size={20} />
                                    <span className="font-bold text-sm">EXIT</span>
                                </button>
                            )}

                            <h1 className="text-lg font-semibold text-[var(--text-primary)]">
                                {(navItems.find((item) => item.path === location.pathname))?.label || 'Dashboard'}
                            </h1>

                            {/* Active Project Badge */}
                            {activeProject && (
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-500/10 border border-primary-500/30 rounded-lg">
                                    <Folder size={16} className="text-primary-500" />
                                    <span className="text-sm font-medium text-primary-500">
                                        {activeProject}
                                    </span>
                                </div>
                            )}

                            {/* Sync Status */}
                            <div className="flex items-center gap-2 px-2 py-1 bg-green-500/10 border border-green-500/30 rounded-lg">
                                <Check size={14} className="text-green-500" />
                                <span className="text-xs font-medium text-green-600">Saved</span>
                                <Cloud size={14} className="text-green-500" />
                                <span className="text-xs font-medium text-green-600">Synced</span>
                            </div>
                        </div>

                        {/* Right side - Actions */}
                        <div className="flex items-center gap-4">
                            <PatentPendingIcon />

                            {/* Full Screen Toggle */}
                            <button
                                onClick={() => setIsFullScreen(!isFullScreen)}
                                className={`
                                    flex items-center gap-2 px-4 py-2 rounded-xl transition-all shadow-md border-2 
                                    ${isFullScreen
                                        ? 'border-red-500 bg-red-500 text-white hover:bg-red-600'
                                        : 'border-blue-500/50 bg-blue-500 text-white hover:bg-blue-600'
                                    }
                                `}
                                title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                            >
                                {isFullScreen ? (
                                    <>
                                        <Minimize size={20} />
                                        <span className="font-bold">Exit Focus</span>
                                    </>
                                ) : (
                                    <>
                                        <Maximize size={20} />
                                        <span className="font-bold">Focus Mode</span>
                                    </>
                                )}
                            </button>

                            <button
                                onClick={() => setIsLearnOpen(true)}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-all shadow-sm border-2 border-blue-500 bg-blue-500 text-white"
                                title="Open Magnus Academy"
                            >
                                <GraduationCap size={18} />
                                <span className="font-bold">Academy</span>
                            </button>

                            <button
                                onClick={startTour}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-all shadow-sm border-2 border-primary-500/50 bg-primary-500/10"
                                title="Start Product Tour"
                            >
                                <Map size={18} className="text-primary-500" />
                                <span className="text-primary-500 font-medium">Tour</span>
                            </button>

                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-full hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                                title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                            >
                                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                            </button>

                            <button
                                onClick={() => navigate('/settings')}
                                className="p-2 rounded-full hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                                title="Settings"
                            >
                                <Settings size={20} />
                            </button>

                            <button
                                onClick={startTour}
                                className="p-2 rounded-full hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                                title="Help - Start Tour"
                            >
                                <HelpCircle size={20} />
                            </button>
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-1 overflow-auto">
                    <div className="h-full">
                        <Outlet />
                    </div>
                </main>

                {/* Footer */}
                <Footer />
            </div >

            {/* Magnus AI Assistant */}
            {!isFullScreen && <MagnusAssistant />}

            {/* Magnus Academy Sidebar */}
            <LearnSidebar />
        </div >
    );
};

export default Layout;
